import React from 'react';
import './PageHeader.css';

/**
 * Standardized page header for the IC Control Tower.
 * Renders: eyebrow label → title (with green period) → subtitle,
 * left-aligned with a hairline bottom border.
 *
 * @param {{
 *   eyebrow?: string,
 *   title?: import('react').ReactNode,
 *   subtitle?: import('react').ReactNode,
 *   meta?: import('react').ReactNode
 * }} props
 *   eyebrow  – small uppercase label (e.g. "Command Center")
 *   title    – main heading text
 *   subtitle – description line (string or node)
 *   meta     – optional right-side content (e.g. date pill)
 */
export default function PageHeader({ eyebrow, title, subtitle, meta }) {
  return (
    <div className="ic-page-header">
      <div className="ic-page-header__text">
        {eyebrow && <p className="ic-page-header__eyebrow">{eyebrow}</p>}
        <h1 className="ic-page-header__title">{title}</h1>
        {subtitle && <p className="ic-page-header__subtitle">{subtitle}</p>}
      </div>
      {meta && <div className="ic-page-header__meta">{meta}</div>}
    </div>
  );
}

/**
 * LIVE date pill — green pulsing dot + "LIVE" label + optional date content.
 * Used in the PageHeader meta slot (right side).
 *
 * @param {{
 *   dateString?: import('react').ReactNode,
 *   small?: boolean,
 *   style?: import('react').CSSProperties
 * }} props
 *   dateString – optional content to show after the separator
 *   small      – renders a compact variant (e.g. inside cards)
 *   style      – optional inline style overrides
 */
export function LiveDatePill({ dateString, small, style }) {
  return (
    <div className={`ic-datepill${small ? ' ic-datepill--small' : ''}`} style={style}>
      <span className="ic-datepill__pulse"><span className="ic-datepill__dot" /></span>
      <span className="ic-datepill__label">LIVE</span>
      {dateString && (
        <>
          <span className="ic-datepill__sep" />
          <span className="ic-datepill__date">{dateString}</span>
        </>
      )}
    </div>
  );
}

/**
 * Standard last-updated strip for pages that do not use a LIVE date pill.
 *
 * @param {{
 *   dateString?: import('react').ReactNode,
 *   children?: import('react').ReactNode
 * }} props
 *   dateString – timestamp or fallback text to display
 *   children   – optional trailing metadata, such as a status key
 */
export function LastUpdated({ dateString, children }) {
  return (
    <div className="ic-last-updated">
      <span className="ic-last-updated__icon" aria-hidden="true">
        <i className="bi bi-clock" />
      </span>
      <span className="ic-last-updated__text">{dateString}</span>
      {children && <div className="ic-last-updated__meta">{children}</div>}
    </div>
  );
}
